import Visibility from "@components/icones/visibility.tsx";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import {
  DataRequest,
  PriorityLibelleMap,
  renameFile,
  TableAgentExterne,
} from "../dashbord.model.ts";
import StatusBody from "@modules/plaintes/complaintManaging/listingComplaint/statusBody.tsx";

import { loadingExternData } from "@core/slice/DashboardExterneSlice.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import Spinner from "@shared/Spinner.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { getFileExternAgent } from "@core/actions/dashboardExterneAction.ts";
interface ComplaintReportTableProps {
  handleShowUpload: () => void;
  dataTableList?: TableAgentExterne[];
  dataRequest: DataRequest;
}
const ComplaintReportTable: React.FC<ComplaintReportTableProps> = ({
  handleShowUpload,
  dataTableList,
  dataRequest,
}) => {
  const navigate = useNavigate();
  const priorityMap = PriorityLibelleMap;
  const dispatch = useAppDispatch();
  const { loadingExport } = useAppSelector((state) => state.dashboardExterne);

  const isLoading = useAppSelector(loadingExternData);

  const viewDetail = (id: number) => {
    navigate(`/complaint/detail-complaint/${id}?type=external`);
  };

  const statusBody = (rowData: TableAgentExterne) => (
    <StatusBody status={rowData.statutActuel} />
  );

  const hanleExportClick = async () => {
    const resultAction = await dispatch(getFileExternAgent(dataRequest));
    if (getFileExternAgent.fulfilled.match(resultAction)) {
      const fileName = `rapport_${renameFile()}_agent externe.xlsx`;
      const blob = resultAction.payload as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleExportModel = () => {
    const link = document.createElement("a");
    link.href = "/assets/modele_import.xlsx";
    link.download = "modele_import.xlsx";
    link.click();
  };

  const actionBodyTemplate = (rowData: TableAgentExterne) => (
    <div className="flex items-center space-x-2">
      <Button
        tooltip="Voir"
        tooltipOptions={{ position: "left" }}
        onClick={(e) => {
          e.preventDefault();
          viewDetail(rowData.plainteId);
        }}
      >
        <Visibility color="#7F868D" />
      </Button>
    </div>
  );

  return (
    <div className="bg-gray-50 rounded-lg shadow-md w-full overflow-x-auto">
      <div className="p-4 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center text-md font-semibold text-gray-600 border-b border-gray-300 pb-2">
        <p>Rapport des interventions réalisées</p>
        <div className="flex flex-wrap gap-2">
          <Button
            className="p-3 sm:w-[225px] w-full h-[32px] rounded-md border border-[#086d33] focus:outline-none focus:ring-1 focus:ring-[#086d33] flex justify-between items-center"
            onClick={handleExportModel}
          >
            <span style={{ color: "var(--primary-color)" }}>
              Télécharger un modèle
            </span>
            <i
              className="fas fa-download"
              style={{ color: "var(--primary-color)" }}
            ></i>
          </Button>
          <Button
            className="p-3 sm:w-[120px] w-full h-[32px] rounded-md border border-[#086d33] flex justify-between items-center"
            onClick={handleShowUpload}
          >
            <span style={{ color: "var(--primary-color)" }}>Importer</span>
            <i
              className="fas fa-download"
              style={{ color: "var(--primary-color)" }}
            ></i>
          </Button>
          <Button
            className="p-3 sm:w-[120px] w-full h-[32px] rounded-md border border-[#086d33] flex justify-between items-center"
            onClick={hanleExportClick}
            disabled={loadingExport === FetchStatusType.LOADING}
          >
            <span style={{ color: "var(--primary-color)", fontSize: "12px" }}>
              {loadingExport === FetchStatusType.LOADING
                ? "Téléchargement"
                : "Exporter"}
            </span>
            {loadingExport === FetchStatusType.LOADING ? (
              <i className="pi pi-spin pi-spinner" />
            ) : (
              <i
                className="fas fa-upload"
                style={{ color: "var(--primary-color)" }}
              ></i>
            )}
          </Button>
        </div>
      </div>
      <div className="m-6 overflow-x-auto">
        <div style={{minWidth: "1000px"}}>
        <DataTable
          value={isLoading === FetchStatusType.LOADING ? [] : dataTableList}
          removableSort
          stripedRows
          size="small"
          scrollable
          scrollHeight="400px"
          style={{
            width: "100%",
          }}
          emptyMessage={
            isLoading === FetchStatusType.LOADING ? (
              <div className="flex justify-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="flex justify-center py-4">
                Aucune donnée disponible
              </div>
            )
          }
        >
          <Column
            field="externalRef"
            header="ID Plainte"
            sortable
            style={{
              width: "50px",
            }}
          />
          <Column
            field="dateReception"
            header="Date de Réception"
            sortable
            style={{
              width: "200px",
              minWidth: "200px",
            }}
          />
          <Column
            field="category"
            header="Catégorie"
            sortable
            style={{
              width: "100px",
              minWidth: "100px",
            }}
          />
          <Column
            field="typePlainte"
            header="Type de Plainte"
            sortable
            style={{
              width: "250px",
              minWidth: "250px",
            }}
          />
          <Column
            field="priority"
            header="Priorité"
            sortable
            style={{
              width: "100px",
              minWidth: "100px",
            }}
            body={(rowData: TableAgentExterne) => {
              return <span>{priorityMap[rowData.priority] || "Inconnu"}</span>;
            }}
          />
          <Column
            field="description"
            header="Description"
            sortable
            style={{
              width: "300px",
              minWidth: "300px",
              maxWidth: "300px",
              height: "50px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          />
          <Column
            field="statutActuel"
            header="Statut actuel"
            sortable
            style={{
              width: "100px",
              minWidth: "100px",
            }}
            body={statusBody}
          />
          <Column
            field="dateResolution"
            header="Date de Résolution"
            sortable
            style={{
              width: "200px",
              minWidth: "200px",
            }}
          />
          <Column
            field="commentaire"
            header="Commentaires"
            sortable
            style={{
              width: "200px",
              minWidth: "200px",
              maxWidth: "200px",
              height: "50px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          />
          <Column body={actionBodyTemplate} header="" align={"center"} />
        </DataTable>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportTable;
